<template>
  <div class="dart-select" :class="{'is-dark' : darkTheme}" v-click-outside="hideOptions">
    <!-- Select header -->
    <div
      class="level dart-select__header"
      :class="{'is-dark': darkTheme }"
      tabindex="0"
      @click="toggleOptions"
      @keypress.space.prevent="toggleOptions"
      @keypress.enter="toggleOptions"
      @keydown.esc="hideOptions"
      role="combobox"
      :aria-haspopup="isOpen"
    >
      <!-- Select label -->
      <div class="level-left dart-select__label" :class="{'is-dark': darkTheme}">
        <span class="level-item">{{label}}</span>
      </div>
      <div class="level-right dart-select__icon" :class="{'is-dark': darkTheme }">
        <i8-icon
          class="level-item"
          :icon="isOpen ? 'chevron-up' : 'chevron-down'"
          :class="{'is-dark': darkTheme}"
          :colour="darkTheme ? 'white': ''"
        />
      </div>
    </div>

    <!-- Export options -->
    <ul class="dart-select__options" :class="{'is-dark': darkTheme}" v-if="isOpen">
      <!-- All Options -->
      <li
        v-if="optional"
        :data-ci="`${dataCiTag}__all}`"
        :class="optionClasses(selected === null)"
        @click="selectOption()"
        v-t="`${langRoot}.allLabel`"
      />

      <!-- Main Options -->
      <li
        v-for="(option, optionIndex) in options"
        :key="`option-${optionIndex}`"
        :value="option.data"
        :data-ci="`${dataCiTag}__${optionId(option)}`"
        :class="optionClasses(option === selected)"
        @click="selectOption(option)"
      >
        <span>{{optionLabel(option)}}</span>
        <slot name="option" :isSelected="selected === option">
        <i8-icon
            v-if="selected === option"            
            class="dart-select__option__icon"
            icon="agree"
            colour="white"
          />
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
const name = 'dart-select';

import ClickOutside from '@shared/directives/click-outside';
import I8Icon from '@shared/components/I8Icon';

export default {
    name,
    componentName: name,
    components: {
        [I8Icon.name]: I8Icon,
    },
    directives: {
        ClickOutside,
    },

    props: {
        value: { type: [Object, String], default: () => {} },
        options: { type: Array, default: () => [] },
        labels: { type: Object, default: () => {} },
        optional: { type: Boolean, default: false },
        idKey: { type: String, default: null },
        staticLabel: { type: String, default: null },
        dataCiTag: { default: '', type: String },
        langRoot: {
            type: String,
        },
        darkTheme: { default: false, type: Boolean },
    },

    data() {
        return {
            isOpen: false,
            selected: null,
        };
    },

    mounted() {
        this.selected = this.value;
    },

    computed: {
        label() {
            if (this.staticLabel) {
                return this.staticLabel;
            }

            if (!this.selected) {
                return this.$t(`${this.langRoot}.allLabel`);
            }

            return this.optionLabel(this.selected);
        },
    },

    methods: {
        showOptions() {
            this.isOpen = true;
        },

        hideOptions() {
            this.isOpen = false;
        },

        toggleOptions() {
            this.isOpen = !this.isOpen;
        },

        selectHightlighted() {
            this.selectOption(this.options[this.highlighted]);
        },

        selectOption(option) {
            this.selected = option;

            this.$emit('input', this.selected);
            this.$emit('change', this.selected);

            this.hideOptions();
        },

        prevOption() {
            if (!this.isOpen) {
                this.showOptions();
            }

            if (this.highlighted > 0) {
                this.highlighted--;

                const option = this.$refs[[`option-${this.highlighted}`]];

                if (option) {
                    this.scroll(Math.abs(option[0].offsetHeight) * -1);
                }
            }
        },

        nextOption() {
            if (!this.isOpen) {
                this.showOptions();
            }

            if (this.highlighted < this.options.length - 1) {
                this.highlighted++;

                const option = this.$refs[[`option-${this.highlighted}`]];

                if (option) {
                    this.scroll(Math.abs(option[0].offsetHeight));
                }
            }
        },

        scroll(offset) {
            let container = this.$el.querySelector(
                `#dart--select__options--${this._uid}`
            );
            container.scrollTop += offset;
        },

        optionLabel(option) {
            return this.labels[this.optionId(option)];
        },

        optionId(option) {
            return this.idKey ? option[this.idKey] : option;
        },

        optionClasses(isHighlighted) {
            return {
                'dart-select__option': true,
                'is-dark': this.darkTheme,
                'dart-select__option--hl': isHighlighted,
            };
        },
    },

    watch: {
        value(newVal, oldVal) {
            if (this.$_.isEqual(newVal, oldVal)) {
                return;
            }

            this.selected = newVal;
        },
    },
};
</script>



// WEBPACK FOOTER //
// shared/components/DartSelect.vue